import { useMemo, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { FileText, Plus } from 'lucide-react';
import type { Facility } from '@cortex/domain';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/shadcn/button';
import { Input } from '@/components/ui/shadcn/input';
import { Skeleton } from '@/components/ui/shadcn/skeleton';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { EmptyState } from '@/components/ui/EmptyState';

const formatDate = (iso: string) => format(parseISO(iso), 'dd MMM yyyy');

/**
 * Master pane for the Facilities tab.
 *
 * Pure presentation — data and selection come from props. Built on the
 * same shadcn primitives as EntityList so the admin shell stays consistent.
 *
 * Each row shows:
 *   - Entity name (broker's legal name)
 *   - Version number (v1, v2, …)
 *   - Active/Inactive badge
 *   - Valid from/to dates
 *
 * Filtering is done client-side against the full list returned by the
 * server (all facilities fit comfortably in memory for v1).
 */

const FILTER_SELECT =
  'h-8 rounded-md border border-input bg-background px-2 text-xs shadow-xs transition-colors focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/40 focus-visible:outline-none';

export interface FacilityListProps {
  readonly facilities: readonly Facility[];
  readonly selectedFacilityId: string | null;
  readonly isLoading: boolean;
  readonly errorMessage?: string | null;
  readonly canCreate: boolean;
  readonly onSelect: (facilityId: string) => void;
  readonly onCreateNew: () => void;
}

type ActiveFilter = 'all' | 'active' | 'inactive';

export function FacilityList({
  facilities,
  selectedFacilityId,
  isLoading,
  errorMessage,
  canCreate,
  onSelect,
  onCreateNew,
}: FacilityListProps) {
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('all');
  const [entityFilter, setEntityFilter] = useState('');
  const [search, setSearch] = useState('');

  const knownEntities = useMemo(() => {
    const map = new Map<string, string>();
    for (const f of facilities) map.set(f.entityId, f.entityName);
    return [...map.entries()].sort(([, a], [, b]) => a.localeCompare(b));
  }, [facilities]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return facilities.filter((f) => {
      if (activeFilter === 'active' && !f.isActive) return false;
      if (activeFilter === 'inactive' && f.isActive) return false;
      if (entityFilter !== '' && f.entityId !== entityFilter) return false;
      if (needle.length > 0 && !f.entityName.toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [facilities, activeFilter, entityFilter, search]);

  const hasFilters = activeFilter !== 'all' || entityFilter !== '' || search !== '';

  return (
    <>
      <div className="space-y-2 border-b border-border bg-muted/40 px-3 py-3">
        <div className="flex gap-2">
          <select
            value={entityFilter}
            onChange={(e) => setEntityFilter(e.target.value)}
            className={cn(FILTER_SELECT, 'flex-1')}
            aria-label="Filter by entity"
          >
            <option value="">All brokerages</option>
            {knownEntities.map(([id, name]) => (
              <option key={id} value={id}>
                {name}
              </option>
            ))}
          </select>
          <select
            value={activeFilter}
            onChange={(e) => setActiveFilter(e.target.value as ActiveFilter)}
            className={FILTER_SELECT}
            aria-label="Filter by status"
          >
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
        <Input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by entity name…"
          className="h-8 text-xs"
          aria-label="Search facilities"
        />
        {canCreate && (
          <Button type="button" onClick={onCreateNew} size="sm" className="w-full">
            <Plus className="h-4 w-4" aria-hidden="true" />
            New facility
          </Button>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {isLoading && <ListSkeleton />}
        {!isLoading && errorMessage && (
          <div
            className="border-b border-destructive/20 bg-destructive/10 px-4 py-6 text-center text-xs text-destructive"
            role="alert"
          >
            {errorMessage}
          </div>
        )}
        {!isLoading && !errorMessage && filtered.length === 0 && (
          <EmptyState
            icon={FileText}
            title={hasFilters ? 'No facilities match these filters.' : 'No facilities yet.'}
            description={
              hasFilters
                ? 'Try clearing the search or changing the filters.'
                : canCreate
                  ? 'Use "New facility" above to create the first agreement.'
                  : 'No facility agreements have been configured yet.'
            }
          />
        )}
        {!isLoading && !errorMessage && filtered.length > 0 && (
          <ul className="divide-y divide-border" data-testid="facility-list">
            {filtered.map((facility) => {
              const selected = selectedFacilityId === facility.id;
              return (
                <li key={facility.id}>
                  <button
                    type="button"
                    onClick={() => onSelect(facility.id)}
                    className={cn(
                      'w-full border-l-4 px-4 py-3 text-left transition-colors focus:bg-accent focus:outline-none',
                      selected
                        ? 'border-l-primary bg-primary/5'
                        : 'border-l-transparent hover:bg-accent',
                    )}
                    aria-current={selected ? 'true' : undefined}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-xs font-bold text-foreground">
                          {facility.entityName}
                        </div>
                        <div className="mt-0.5 font-mono text-xs text-muted-foreground">
                          v{facility.version} · {formatDate(facility.validFrom)} –{' '}
                          {formatDate(facility.validTo)}
                        </div>
                      </div>
                      <StatusBadge
                        tone={facility.isActive ? 'active' : 'neutral'}
                        label={facility.isActive ? 'Active' : 'Inactive'}
                      />
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </>
  );
}

function ListSkeleton() {
  return (
    <ul className="divide-y divide-border" aria-hidden="true">
      {[0, 1, 2, 3].map((i) => (
        <li key={i} className="space-y-2 px-4 py-3">
          <Skeleton className="h-3 w-3/4" />
          <Skeleton className="h-2 w-1/2" />
        </li>
      ))}
    </ul>
  );
}
